import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ActionCard {
  badge: string;
  badgeVariant?: 'default' | 'outline';
  title: string;
  description: string;
  bullets: string[];
  ctaLabel: string;
  ctaLink: string;
  microText: string;
  isPrimary?: boolean;
}

const actionCards: ActionCard[] = [
  {
    badge: 'Recommended',
    badgeVariant: 'default',
    title: 'Get a free 1-hour consultation',
    description:
      "I'll review your domain, your current situation, and your goals. You'll leave with a clear recommendation on whether Starter, Basic, Pro, or a custom option makes the most sense — even if we don't work together.",
    bullets: [
      'Honest feedback on your domain, niche, and positioning.',
      'Clarity on the right package and realistic timeline.',
      'Space to ask questions about process, scope, and budget.',
    ],
    ctaLabel: 'Book my free consult',
    ctaLink: '/services/consult', // TODO: Wire to actual consult flow
    microText: 'No pressure, no hard pitch. Just a focused working session.',
    isPrimary: true,
  },
  {
    badge: 'I know what I want',
    badgeVariant: 'outline',
    title: 'Get a personalized service proposal',
    description:
      "If you already have a strong idea of what you need, skip straight to a tailored proposal. Share your domain, current assets, and goals, and I'll map out a plan, scope, and pricing for your situation.",
    bullets: [
      'Designed for people who already know their rough scope.',
      'Clear breakdown of deliverables, timeline, and investment.',
      'Option to include a phased or staged approach if needed.',
    ],
    ctaLabel: 'Request my proposal',
    ctaLink: '/services/proposal', // TODO: Wire to actual proposal flow
    microText: "Ideal if you're comparing options or working with a specific budget window.",
    isPrimary: false,
  },
];

export default function ServicesActionsSection() {
  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' as const },
    },
  };

  return (
    <>
      {/* Main Section */}
      <section className="relative w-full bg-black text-slate-100 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              ref={sectionReveal.ref}
              variants={containerVariants}
              initial="hidden"
              animate={sectionReveal.controls}
            >
              {/* Heading Section */}
              <div className="text-center mb-12 md:mb-16">
                <h2
                  ref={titleRef as any}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 mb-6"
                >
                  Pick your next step
                </h2>
                <p
                  ref={subtitleRef as any}
                  className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
                >
                  Whether you're still figuring things out or you already know what you want, there's a simple way to move forward.
                </p>
              </div>

              {/* Two Action Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {actionCards.map((card, index) => (
                  <motion.div key={index} variants={cardVariants}>
                    <Card
                      className={cn(
                        'h-full bg-slate-900 border-slate-800 rounded-xl transition-all duration-200',
                        'hover:-translate-y-1 hover:border-slate-500 hover:shadow-xl',
                        card.isPrimary && 'ring-1 ring-blue-500/20'
                      )}
                    >
                      <CardHeader className="pb-4">
                        <div className="mb-3">
                          <Badge
                            variant={card.badgeVariant || 'outline'}
                            className={cn(
                              'text-xs',
                              card.badgeVariant === 'default' &&
                                'bg-blue-600 text-white border-blue-600'
                            )}
                          >
                            {card.badge}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl md:text-2xl font-semibold text-slate-50">
                          {card.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Description */}
                        <p className="text-slate-300 leading-relaxed">{card.description}</p>

                        {/* Bullet List */}
                        <ul className="space-y-2">
                          {card.bullets.map((bullet, bulletIndex) => (
                            <li key={bulletIndex} className="flex items-start gap-2 text-slate-300">
                              <span className="text-blue-400 mt-1.5 shrink-0">•</span>
                              <span className="text-sm">{bullet}</span>
                            </li>
                          ))}
                        </ul>

                        {/* CTA Button */}
                        <div className="pt-2">
                          <Button
                            asChild
                            size="lg"
                            className={cn(
                              'w-full bg-white text-slate-900 hover:bg-slate-100',
                              'hover:-translate-y-0.5 hover:shadow-lg',
                              'transition-all duration-200',
                              card.isPrimary && 'hover:shadow-xl'
                            )}
                          >
                            <Link to={card.ctaLink}>{card.ctaLabel}</Link>
                          </Button>

                          {/* Micro Text */}
                          <p className="text-xs text-slate-400 mt-3 text-center">
                            {card.microText}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="w-full h-8 bg-gradient-to-b from-black via-neutral-200 to-white" />
    </>
  );
}

